import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  AddUserIcon,
  CircularLoader,
  InfoIcon,
  PencilIcon,
  PlaneFilledIcon,
  Tooltip,
} from "components";
import { useGlobalStore } from "controller/store/globalStore";
import { useAtomValue, useSetAtom } from "jotai";
import {
  memo,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  DictationMicButton,
  DictationView,
  dictationIssueMessage,
  EDITOR_PUSH_TO_TALK_HOLD_DELAY_MS,
  insertDictatedText,
  playDictationEndSound,
  playDictationStartSound,
  useDictationPushToTalk,
  usePushToTalkScope,
  useVoiceDictation,
} from "shared/features/voice-dictation";
import type {
  AccessSpecifier,
  AttachmentResultType,
  ChatMetaData,
} from "shared/types";
import { AttachmentButton } from "./AttachmentButton";
import { ChatAttachmentPreview } from "./ChatAttachmentPreview";
import { ChatFormattingToolbar } from "./ChatFormattingToolbar";
import { ChatModeSwitcher } from "./ChatModeSwitcher";
import { useChatDraft } from "./hooks/useChatDraft";
import { useFileAttachmentUpload } from "./hooks/useFileAttachmentUpload";
import { ReplyPreview } from "./ReplyPreview";
import {
  chatCustomerMembers,
  chatExternalMembers,
  chatFileAttachments,
  chatHasErrorFiles,
  chatHasUploadingFiles,
  chatImageUploadError,
  chatIsUploadingImages,
  chatMembers,
  chatReplyToMessage,
  chatSendMode,
  clearChatFileAttachments,
  clearChatReplyToMessage,
  removeChatFileAttachment,
  setChatImageUploadError,
  setChatSendMode,
  setChatUploadingImages,
  setModeBannerVisible,
} from "./store";
import {
  ChatMessageInputRef,
  type MentionPresence,
  TipTapChatInput,
} from "./TipTapChatInput";
import { appendTextToChatDraft } from "./utils/chatDraftStorage";
import { stripHtml } from "./utils/stripHtml";

const isMacOS =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const sendShortcutHint = isMacOS
  ? "Enter→new line, Cmd+Enter→send"
  : "Enter→new line, Ctrl+Enter→send";

interface ChatInputSectionProps {
  serviceType: "ticket" | "task";
  serviceId: string;
  serviceSource?: string;
  isDisabled: boolean;
  isTriageInProgress?: boolean;
  metaData?: ChatMetaData;
  isSendingMessage: boolean;
  editorRef: RefObject<ChatMessageInputRef | null>;
  hideModeSwitcher?: boolean;
  disabledModeSwitcher?: boolean;
  sendMode?: AccessSpecifier;
  onModeChange?: (mode: AccessSpecifier) => void;
  onManageParticipantsClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onSend: (params: {
    messageText: string;
    attachmentArray?: Array<{
      mapping_id: string;
      file_type: string;
      url: string;
      original_name: string;
    }>;
    mentionedUsers?: string[];
    mentionedEmails?: string[];
    parentMessageId?: string;
    excerpt?: string;
  }) => void;
}

export const ChatInputSection = memo(
  ({
    serviceType,
    serviceId,
    serviceSource,
    isDisabled,
    isTriageInProgress,
    metaData,
    isSendingMessage,
    editorRef,
    hideModeSwitcher,
    disabledModeSwitcher,
    sendMode,
    onManageParticipantsClick,
    onModeChange,
    onSend,
  }: ChatInputSectionProps) => {
    const theme = useTheme();
    const [isFormattingToolbarVisible, setIsFormattingToolbarVisible] =
      useState(false);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [hasMention, setHasMention] = useState(false);
    const [hasNonResourceMention, setHasNonResourceMention] = useState(false);

    // ─── Jotai Atoms ──────────────────────────────────────────────
    const sendMessageAccessSpecifier = useAtomValue(chatSendMode);
    const setSendMode = useSetAtom(setChatSendMode);
    const isUploadingImages = useAtomValue(chatIsUploadingImages);
    const imageUploadError = useAtomValue(chatImageUploadError);
    const clearImageUploadError = useSetAtom(setChatImageUploadError);
    const setUploading = useSetAtom(setChatUploadingImages);
    const setBannerVisible = useSetAtom(setModeBannerVisible);
    const members = useAtomValue(chatMembers);
    const customerUsers = useAtomValue(chatCustomerMembers);
    const externalUsers = useAtomValue(chatExternalMembers);
    const replyToMessage = useAtomValue(chatReplyToMessage);
    const clearReply = useSetAtom(clearChatReplyToMessage);

    // ─── File Attachment Atoms ────────────────────────────────────
    const fileAttachments = useAtomValue(chatFileAttachments);
    const hasUploadingFiles = useAtomValue(chatHasUploadingFiles);
    const hasErrorFiles = useAtomValue(chatHasErrorFiles);
    const removeFile = useSetAtom(removeChatFileAttachment);
    const clearFiles = useSetAtom(clearChatFileAttachments);

    // ─── File Upload Hook ─────────────────────────────────────────
    const { fileInputRef, openFilePicker, handleInputChange } =
      useFileAttachmentUpload({
        serviceId,
        serviceType,
        isDisabled,
      });

    // ─── Draft Persistence (per serviceId, survives refresh) ──────
    const { initialHtml, persistDraft } = useChatDraft({
      serviceId,
      editorRef,
    });

    // ─── Effective mode (thread override or global atom) ───────────
    const effectiveMode = sendMode ?? sendMessageAccessSpecifier;

    // ─── Voice Dictation ──────────────────────────────────────────
    // The recording is uploaded on ✓, as on home, for playback later.
    // Leaving mid-dictation (navigation, or a thread replacing the main
    // composer) lands the words in this service's stored draft, so they're in
    // the editor on return. Callers key this component by serviceId, so the
    // words never reach another ticket.
    const timezone = useGlobalStore((state) => state.timezone);
    const dictation = useVoiceDictation({
      onLeave: ({ text }) =>
        appendTextToChatDraft(serviceId, text, effectiveMode),
    });
    const {
      issue: dictationIssue,
      status: dictationStatus,
      dismissIssue: dismissDictationIssue,
    } = dictation;
    const isDictating = dictationStatus !== "idle";
    const canDictate = dictation.isAvailable && !isDisabled;
    const composerRef = useRef<HTMLDivElement>(null);
    /** The editor is hidden while dictating — focus it once it's back, so Cmd/Ctrl+Enter sends. */
    const shouldFocusEditorRef = useRef(false);

    const startDictation = () => {
      playDictationStartSound();
      void dictation.start();
    };

    // Waits briefly while Soniox finalises the last words; ✗ in that window
    // cancels it and nothing is inserted.
    const acceptDictation = async () => {
      shouldFocusEditorRef.current = true;
      playDictationEndSound();
      // Taken before the wait: editorRef is the parent's, so if this composer
      // is replaced while Soniox finalises it already points at the next
      // ticket's editor.
      const editor = editorRef.current?.getEditor();
      const accepted = await dictation.accept();
      if (!accepted) return;
      if (!editor || editor.isDestroyed) {
        // Left mid-✓ (ticket switched, page left): keep the words in this
        // ticket's draft, as for a dictation interrupted by leaving.
        appendTextToChatDraft(serviceId, accepted.text, effectiveMode);
        return;
      }
      insertDictatedText(editor, accepted.text);
    };

    const discardDictation = () => {
      playDictationEndSound();
      dictation.discard();
    };

    // Works without clicking in first. The ticket screen mounts more than one
    // composer (main chat, the thread panel), so the scope picks exactly one:
    // the focused composer, else the visible one last focused, else the first.
    const pushToTalkScope = usePushToTalkScope({
      ref: composerRef,
      enabled: canDictate,
    });
    useDictationPushToTalk({
      enabled: canDictate,
      status: dictationStatus,
      canAccept: dictation.canAccept,
      holdDelayMs: EDITOR_PUSH_TO_TALK_HOLD_DELAY_MS,
      isAllowedTarget: pushToTalkScope.isAllowedTarget,
      onStart: startDictation,
      onAccept: () => void acceptDictation(),
      onDiscard: discardDictation,
    });

    // Dictation that couldn't start (or died before any words) is back to the
    // editor, so the reason goes in a toast. A stream that stopped with words
    // kept shows its reason inside the dictation box instead.
    useEffect(() => {
      if (!dictationIssue || dictationStatus !== "idle") return;
      toast.error(dictationIssueMessage(dictationIssue, timezone));
      dismissDictationIssue();
    }, [dictationIssue, dictationStatus, dismissDictationIssue, timezone]);

    useEffect(() => {
      if (isDictating || !shouldFocusEditorRef.current) return;
      shouldFocusEditorRef.current = false;
      editorRef.current?.getEditor()?.commands.focus();
    }, [isDictating, editorRef]);

    // ─── Auto-switch to private when replying to a private message ──
    const prevModeBeforeReplyRef = useRef<AccessSpecifier | null>(null);

    useEffect(() => {
      if (replyToMessage?.accessSpecifier === "private") {
        if (prevModeBeforeReplyRef.current === null) {
          prevModeBeforeReplyRef.current = sendMessageAccessSpecifier;
        }
        if (!sendMode) {
          setSendMode("private");
        }
      } else if (prevModeBeforeReplyRef.current !== null) {
        if (!sendMode) {
          setSendMode(prevModeBeforeReplyRef.current);
        }
        prevModeBeforeReplyRef.current = null;
      }
    }, [replyToMessage, sendMode, sendMessageAccessSpecifier, setSendMode]);

    const bannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showModeBanner = useCallback(() => {
      setBannerVisible(true);
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
      bannerTimeoutRef.current = setTimeout(() => {
        setBannerVisible(false);
      }, 3000);
    }, [setBannerVisible]);

    // Auto-clear image upload error after 5 seconds
    useEffect(() => {
      if (!imageUploadError) return;
      const timer = setTimeout(() => {
        clearImageUploadError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }, [imageUploadError, clearImageUploadError]);

    // ─── Persist draft when context atoms change ──────────────────
    // Text changes are persisted via TipTap's onDraftChange; this covers
    // attachment add/remove, reply set/clear, and send-mode switches.
    // persistDraft reads the latest values via refs, so these atoms are
    // listed purely as change triggers.
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger deps
    useEffect(() => {
      persistDraft();
    }, [fileAttachments, replyToMessage, effectiveMode, persistDraft]);

    // ─── Merge file attachments into send payload ─────────────────
    const handleSendWithAttachments = useCallback(
      (params: {
        messageText: string;
        attachmentArray: Array<AttachmentResultType>;
        mentionedUsers?: string[];
        mentionedEmails?: string[];
      }) => {
        const completedFileAttachments = fileAttachments
          .filter((f) => f.status === "Uploaded")
          .map((f) => f.responseData);

        const mergedAttachments = [
          ...(params.attachmentArray ?? []),
          ...completedFileAttachments,
        ];

        // Build reply fields if reply is active
        const replyData = replyToMessage
          ? {
              parentMessageId: replyToMessage.chatId,
              excerpt: stripHtml(replyToMessage.message).slice(0, 500),
            }
          : undefined;

        onSend({
          messageText: params.messageText,
          attachmentArray:
            mergedAttachments.length > 0 ? mergedAttachments : undefined,
          mentionedUsers: params.mentionedUsers,
          mentionedEmails: params.mentionedEmails,
          ...(replyData && {
            parentMessageId: replyData.parentMessageId,
            excerpt: replyData.excerpt,
          }),
        });

        clearFiles();
      },
      [onSend, fileAttachments, clearFiles, replyToMessage],
    );

    // ─── Insert HTML into the editor (e.g. from canned notes) ─────
    const handleInsertHtml = useCallback(
      (html: string) => {
        if (!html) return;
        const editor = editorRef.current?.getEditor?.();
        if (!editor) return;
        editor.chain().focus().insertContent(html).run();
      },
      [editorRef],
    );

    const hasUploadedFiles = fileAttachments.some(
      (f) => f.status === "Uploaded",
    );
    const isSendDisabled =
      isDisabled ||
      isSendingMessage ||
      isUploadingImages ||
      hasUploadingFiles ||
      hasErrorFiles ||
      (isEditorEmpty && !hasUploadedFiles);

    return (
      <Stack
        ref={composerRef}
        {...pushToTalkScope.scopeProps}
        sx={{
          backgroundColor: theme.palette.neutral.main,
          px: "24px",
          border: `8px solid ${theme.palette.neutral.main}`,
          borderTop: 0,
          // paddingBottom: "8px",
          gap: "8px",
          "& .editor-hint": { color: theme.palette.lightBg.low },
          "&:focus-within .editor-hint": { color: theme.palette.purple.main },
        }}
      >
        <Stack
          tabIndex={-1}
          onFocus={() => setIsEditorFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsEditorFocused(false);
            }
          }}
          sx={{
            width: "100%",
            borderRadius: "8px",
            border: isEditorFocused
              ? `1px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.neutral["015"]}`,
            boxShadow: "0px 0px 4px 0px #00000029,  0px 0px 2px 0px #00000014",
            position: "relative",
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* ─── Top: Disabled banner OR Mode Switcher ─────────── */}
          {isDisabled && !isTriageInProgress ? (
            <Stack
              direction="row"
              sx={{
                width: "100%",
                gap: "8px",
                alignItems: "center",
                px: "8px",
                py: "4px",
                bgcolor: theme.palette.secondary["purple-tint-3"],
                borderRadius: "8px 8px 0 0",
              }}
            >
              <InfoIcon sx={{ width: "16px", height: "16px" }} />
              <Typography variant="body-small">
                The {serviceType} is now marked as{" "}
                {metaData?.status_name === "Cancelled" ? "cancelled" : "closed"}
              </Typography>
            </Stack>
          ) : (
            !isTriageInProgress &&
            !hideModeSwitcher &&
            serviceType === "ticket" && (
              <ChatModeSwitcher
                activeMode={effectiveMode}
                customerUsers={customerUsers}
                externalUsers={externalUsers}
                isEditorFocused={isEditorFocused}
                disabled={
                  disabledModeSwitcher ||
                  replyToMessage?.accessSpecifier === "private"
                }
                disabledModes={
                  hasNonResourceMention
                    ? ["private", "self"]
                    : hasMention
                      ? ["self"]
                      : []
                }
                disabledReason={
                  hasNonResourceMention
                    ? "Non-resource user mentioned, cannot change access"
                    : hasMention
                      ? "Mentions present, cannot switch to self"
                      : undefined
                }
                onModeChange={(mode) => {
                  if (onModeChange) {
                    onModeChange(mode);
                  } else {
                    setSendMode(mode);
                  }
                  showModeBanner();
                  setIsEditorFocused(true);
                }}
                onContainerClick={() => setIsEditorFocused(true)}
              />
            )
          )}

          {/* ─── Formatting Toolbar (conditional) ─────────────── */}
          {isFormattingToolbarVisible && !isDisabled && !isDictating && (
            <ChatFormattingToolbar
              editor={editorRef.current?.getEditor() ?? null}
            />
          )}

          {/* ─── Uploading images / error banner ───────────────── */}
          {(isUploadingImages || imageUploadError) && (
            <Stack
              direction="row"
              sx={{
                width: "100%",
                gap: "8px",
                alignItems: "center",
                px: "12px",
                py: "4px",
                bgcolor: imageUploadError
                  ? theme.palette.primitive.system.red[50]
                  : theme.palette.secondary["purple-tint-3"],
              }}
            >
              {imageUploadError ? (
                <>
                  <ErrorOutlineIcon
                    sx={{
                      fontSize: "16px",
                      color: theme.palette.primitive.system.red[800],
                    }}
                  />
                  <Typography
                    variant="body-small"
                    sx={{ color: theme.palette.primitive.system.red[800] }}
                  >
                    {imageUploadError}
                  </Typography>
                </>
              ) : (
                <>
                  <CircularLoader size={16} thickness={4} />
                  <Typography variant="body-small">
                    Uploading images...
                  </Typography>
                </>
              )}
            </Stack>
          )}

          {/* ─── Reply Preview ──────────────────────────────── */}
          {replyToMessage && !isDisabled && (
            <ReplyPreview
              senderDisplayName={replyToMessage.senderDisplayName}
              messageHtml={replyToMessage.message}
              onClose={() => clearReply()}
            />
          )}

          {/* ─── TipTap Editor ────────────────────────────────── */}
          {/* Hidden, not unmounted, while dictating: it keeps the draft and the cursor ✓ inserts at. */}
          <Stack
            sx={{
              display: isDictating ? "none" : "flex",
              backgroundColor: theme.palette.neutral.main,
              width: "100%",
              px: "16px",
              overflow: "hidden",
              borderRadius: "4px",
            }}
          >
            <TipTapChatInput
              key={serviceId}
              ref={editorRef as Ref<ChatMessageInputRef>}
              serviceId={serviceId}
              serviceType={serviceType}
              sendMessageAccessSpecifier={effectiveMode}
              isSendingMessage={isSendingMessage}
              isFileUploading={hasUploadingFiles || hasErrorFiles}
              hasExternalAttachments={hasUploadedFiles}
              isDisabled={isDisabled}
              members={members}
              initialContent={initialHtml}
              onSend={handleSendWithAttachments}
              toggleUploadingStatus={setUploading}
              onContentChange={setIsEditorEmpty}
              onDraftChange={persistDraft}
              onFocusChange={setIsEditorFocused}
              onMentionPresenceChange={useCallback(
                (presence: MentionPresence) => {
                  setHasMention(presence.hasMention);
                  setHasNonResourceMention(presence.hasNonResourceMention);
                },
                [],
              )}
            />
          </Stack>

          {/* ─── File Attachment Preview ───────────────────────── */}
          <ChatAttachmentPreview
            files={fileAttachments}
            onRemove={(key) => removeFile(key)}
            onClearAll={() => clearFiles()}
          />

          {/* ─── Action Bar ───────────────────────────────────── */}
          {/* Hidden while dictating: the dictation box below has its own ✓ / ✗ row. */}
          <Stack
            direction="row"
            sx={{
              display: isDictating ? "none" : "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              px: "16px",
              pb: "8px",
            }}
          >
            <Stack direction="row" sx={{ gap: "12px" }}>
              <Tooltip title="Add participants">
                <Box
                  onClick={
                    isDisabled
                      ? undefined
                      : (e: React.MouseEvent<HTMLElement>) =>
                          onManageParticipantsClick?.(e)
                  }
                >
                  <AddUserIcon
                    sx={{
                      cursor: isDisabled ? "default" : "pointer",
                      color: isDisabled
                        ? theme.palette.neutral["020"]
                        : theme.palette.lightBg.low,
                    }}
                  />
                </Box>
              </Tooltip>
              {/* Pencil — toggles formatting toolbar */}

              <Tooltip title="Formatting toolbar">
                <Box>
                  <PencilIcon
                    onClick={() => setIsFormattingToolbarVisible((v) => !v)}
                    sx={{
                      cursor: "pointer",
                      color: isDisabled
                        ? theme.palette.neutral["020"]
                        : isFormattingToolbarVisible
                          ? theme.palette.primary.main
                          : theme.palette.lightBg.low,
                    }}
                  />
                </Box>
              </Tooltip>

              {/* Canned Notes + Attachment */}
              <AttachmentButton
                serviceId={serviceId}
                serviceType={serviceType}
                isDisabled={isDisabled}
                serviceSource={serviceSource || ""}
                onAttachClick={openFilePicker}
                onInsertHtml={handleInsertHtml}
              />
            </Stack>

            <Stack direction="row" sx={{ alignItems: "center", gap: "12px" }}>
              {canDictate && (
                <DictationMicButton
                  onClick={startDictation}
                  sx={{ p: "2px" }}
                />
              )}
              {/* Send button */}
              <PlaneFilledIcon
                onClick={() => {
                  if (!isSendDisabled) {
                    editorRef.current?.handleExternalSend();
                  }
                }}
                sx={{ cursor: isSendDisabled ? "default" : "pointer" }}
                htmlColor={
                  isSendDisabled
                    ? theme.palette.neutral["020"]
                    : theme.palette.primary.main
                }
              />
            </Stack>
          </Stack>

          {/* ─── Dictation (in place of the editor and action bar) ─ */}
          {isDictating && (
            <Stack sx={{ gap: "8px", px: "16px", py: "8px" }}>
              <DictationView
                status={dictationStatus}
                segments={dictation.segments}
                tail={dictation.tail}
                canAccept={dictation.canAccept}
                issue={dictationIssue}
                elapsedSeconds={dictation.elapsedSeconds}
                maxSeconds={dictation.maxSeconds}
                silenceStopIn={dictation.silenceStopIn}
                levelsRef={dictation.levelsRef}
                onAccept={() => void acceptDictation()}
                onDiscard={discardDictation}
              />
            </Stack>
          )}
        </Stack>
        {/* Hidden, not removed, while dictating — Enter means ✓ there, and the box keeps its height. */}
        <Typography
          className="editor-hint"
          variant="body-xsmall"
          sx={{
            textAlign: "right",
            visibility: isDictating ? "hidden" : "visible",
          }}
        >
          {sendShortcutHint}
        </Typography>
        {/* ─── Hidden file input ──────────────────────────────── */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.doc,.docx,.pdf,.txt,.md,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.zip,.rar,.7z,.json"
          style={{ display: "none" }}
          onChange={handleInputChange}
          aria-label="Attach files"
        />
      </Stack>
    );
  },
);
